import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  Type,
  createComponent,
  inject,
} from '@angular/core';

import { CamColumnRenderData } from '../engine/types';

export interface AngularColumnConfig {
  componentType: Type<any>;
  componentInputs?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ComponentRendererService {
  private _componentRefs = new Map<HTMLElement, ComponentRef<any>>();
  private _appRef = inject(ApplicationRef);
  private _envInjector = inject(EnvironmentInjector);

  render =
    <T>(componentType: Type<T>, inputs?: Partial<T>) =>
    (renderData: CamColumnRenderData) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'angular-component-wrapper';

      try {
        const componentRef = createComponent(componentType, {
          environmentInjector: this._envInjector,
        });

        componentRef.setInput('record', renderData.record);
        componentRef.setInput('column', renderData.column);
        componentRef.setInput('value', renderData.value);
        componentRef.setInput('data', renderData.record?.data);
        Object.entries(inputs ?? {}).forEach(([key, value]) => componentRef.setInput(key, value));

        componentRef.changeDetectorRef.detectChanges();

        this._appRef.attachView(componentRef.hostView);

        const domElement = componentRef.location.nativeElement;
        wrapper.appendChild(domElement);

        this._componentRefs.set(wrapper, componentRef);
      } catch (error) {
        console.error('Error rendering Angular component:', error);
        wrapper.innerHTML = '<span class="error">Component render error</span>';
      }
      return wrapper;
    };

  update<T>(container: HTMLElement, inputs: Partial<T>): void {
    const componentRef = this._componentRefs.get(container);
    if (componentRef) {
      Object.entries(inputs).forEach(([key, value]) => componentRef.setInput(key, value));
      componentRef.changeDetectorRef.detectChanges();
    }
  }

  cleanup(container: HTMLElement): void {
    const componentRef = this._componentRefs.get(container);
    if (componentRef) {
      this._appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      this._componentRefs.delete(container);
      container.innerHTML = '';
    }
  }

  cleanupAll(): void {
    this._componentRefs.forEach((ref, container) => {
      this._appRef.detachView(ref.hostView);
      ref.destroy();
      container.innerHTML = '';
    });
    this._componentRefs.clear();
  }
}
